import { useMsal } from '@azure/msal-react'
import { useSearch } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import useCustomSearch from '@/hooks/useCustomSearch'
import { approvalsAtom, compliancesAtom } from '@/store/tasksApprovals'
import { getApprovalsForWidget, getDueOverDueText, getTasksForWidget } from '@/store/tasksApprovals/utils'
import { widgetsAtom } from '@/store/tasksApprovals/widgets'
import { IWidgetConfig, TIntegratedSystems, TType } from '@/types/tasksApprovals'

import { widgetsConfig } from '../config'
import Widget from './widget'

const Container = ({ isExpanded }: { isExpanded: boolean }) => {
    const { t } = useTranslation()
    const { type }: { type: TType } = useSearch({ from: '/tasks-approvals' })
    const [approvals] = useAtom(approvalsAtom)
    const [compliances] = useAtom(compliancesAtom)
    const { accounts } = useMsal()
    const mud_id: string = accounts[0].idTokenClaims?.UID as string
    const spoofUser = localStorage.getItem('spoofUser')
    const currentUserId: string = spoofUser || mud_id

    const [widgets] = useAtom(widgetsAtom)

    const { updateServices, filters } = useCustomSearch()

    const onClick = (id: TIntegratedSystems) => {
        updateServices(id, type ?? 'all')
    }

    const sortWidgets = (a: string, b: string) => {
        const elA = a.toLowerCase()
        const elB = b.toLowerCase()

        if (elA < elB) {
            return -1
        }
        if (elA > elB) {
            return 1
        }
        return 0
    }

    const getWidgets = () => {
        const _approvals = getApprovalsForWidget(approvals, widgets)
        const _tasks = getTasksForWidget(compliances, currentUserId, widgets)
        const _widgetsWithData = [...[..._approvals, ..._tasks].sort((a, b) => sortWidgets(a.title ?? a.id, b.title ?? b.id))]

        const approvalsIds = new Set(_approvals.map((item) => item.id))
        const tasksIds = new Set(_tasks.map((item) => item.id))

        const _emptyWidgets = [
            ...widgets
                .filter((item) => !approvalsIds.has(item.system) && !tasksIds.has(item.system) && item.active && !item.hide_widget)
                .filter((item) => `${item.system_type}s` === type || type === 'all' || !type)
                // remove MY_LEARNING_RO from empty widgets list
                .filter((item) => item.system !== 'MY_LEARNING_RO')
                .sort((a, b) => sortWidgets(a.widget_title ?? a.system, b.widget_title ?? b.system)),
        ]

        return {
            widgetsWithData: _widgetsWithData,
            emptyWidgets: _emptyWidgets,
        }
    }

    const { emptyWidgets, widgetsWithData } = getWidgets()

    useEffect(() => {
        if (emptyWidgets.length) {
            const filtersToClear: TIntegratedSystems[] = []

            emptyWidgets.forEach((widget) => {
                if (filters.services[type ?? 'all'].includes(widget.id)) {
                    filtersToClear.push(widget.id)
                }
            })

            if (filtersToClear.length) {
                updateServices(null, type ?? 'all', filtersToClear, { replace: true })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emptyWidgets])

    return (
        <Box>
            {widgetsWithData.map((item) => {
                const config: IWidgetConfig = widgetsConfig(item.id)

                let _bodyText = t('tasksApprovals.widgets.waiting_for')

                if (item.type === 'tasks') {
                    if (item.over_due) _bodyText = getDueOverDueText(item.over_due, 'overdue')
                    else if (item.due_in) _bodyText = getDueOverDueText(item.due_in, 'duein')
                }

                const _days = item.type === 'approvals' ? item.days : item.over_due || item.due_in
                const _howMany =
                    item.type === 'approvals' ? item.countHigherItem : item.over_due ? item.countHigherItemOverDue : item.countHigherItemDue

                const _isCompliance = item.type === 'tasks'
                const _myCompliances = item.type === 'tasks' ? item.myCompliances : undefined
                const _directsCompliances = item.type === 'tasks' ? item.directsCompliance : undefined
                const _isSelected =
                    (type === item.type && filters.services[item.type].includes(item.id)) ||
                    (type === 'all' && filters.services.all.includes(item.id))

                if (type === 'all' || type == item.type || !type) {
                    return (
                        <Widget
                            key={`${item.id}-${item.type}`}
                            id={item.id}
                            title={item?.title ?? item.id}
                            requestNumber={item.count}
                            color={config.color}
                            days={_days}
                            howMany={_howMany}
                            bodyText={_bodyText}
                            onClick={onClick}
                            type={item.type}
                            link={item?.url ?? ''}
                            isSelected={_isSelected}
                            isCompliance={_isCompliance}
                            isExpanded={isExpanded}
                            myCompliances={_myCompliances}
                            directsCompliances={_directsCompliances}
                            status={item.type === 'approvals' ? 'waiting' : item.over_due ? 'overdue' : 'duein'}
                        />
                    )
                }
            })}

            {emptyWidgets &&
                emptyWidgets.map((item, index) => {
                    return (
                        <Widget
                            key={`${item.id}-${index}`}
                            id={item.id}
                            title={item.widget_title ?? item.system}
                            requestNumber={0}
                            color={item.color}
                            days={0}
                            howMany={0}
                            bodyText=""
                            onClick={onClick}
                            type={'all'}
                            link={item.widget_url}
                            isSelected={false}
                            isCompliance={false}
                            isExpanded={isExpanded}
                            isUnderMaintenance={item.under_maintenance}
                        />
                    )
                })}
        </Box>
    )
}

export default Container
